import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { SxProps, Theme } from '@mui/material/styles'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardMedia from '@mui/material/CardMedia'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import NoResults from '@/assets/svg/no-results.svg?react'

import ConsultationIcon from '@/components/ui/icons/Consultation'

import ConsultationsTable, { ConsultationData } from '@/pages/consultations/components/ConsultationsTable'

export type ConsultationsCardProps = {
  isDoctor?: boolean
  consultations: ConsultationData[]
  onAcceptConsultation?: (consultationId: string) => void
  onRefuseConsultation?: (consultationId: string) => void
  onFilterChange?: (filter: string) => void
  emptyMessage: string
  filters?: {
    id: string
    title: string
    activeColor: string
    badgeContent: number
    selected: boolean
  }[]
  sx?: SxProps<Theme>

  onPageChange: (newPage: number) => void
  onRowsPerPageChange: (newRowsPerPage: number) => void
  totalCount: number
}


export default function ConsultationsCard(props: ConsultationsCardProps) {
  const { isDoctor = false, consultations, emptyMessage, filters = [], onFilterChange, sx = [], onPageChange, onRowsPerPageChange, totalCount, onAcceptConsultation, onRefuseConsultation } = props
  const { t } = useTranslation()

  return (
    <Card sx={[{ height: '100%' }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Stack
        direction={{ xs: 'column-reverse', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        paddingX={3}
        paddingY={3}
        spacing={2}
      >
        <Stack divider={<Divider orientation="vertical" flexItem />} direction="row" justifyContent="flex-start" alignItems="center" spacing={3}>
          {filters.map((filter) => {
            return (
              <Button
                key={filter.id}
                variant="neutral"
                onClick={() => onFilterChange?.(filter.id)}
                sx={[
                  { color: filter.selected ? filter.activeColor : 'grey', cursor: 'pointer', fontWeight: 500 },
                  filter.selected && {
                    borderColor: filter.activeColor,
                  },
                ]}
              >
                <Badge
                  key={filter.id}
                  invisible={false}
                  badgeContent={filter.badgeContent}
                  color="primary"
                  showZero
                  sx={{
                    '& .MuiBadge-badge': {
                      right: '-16px',
                      top: '-6px',
                      padding: '0 4px',
                      fontSize: 12,
                      height: 24,
                      minWidth: 24,
                      borderRadius: '100%',
                      backgroundColor: filter.activeColor,
                    },
                  }}
                >
                  {filter.title}
                </Badge>
              </Button>
            )
          })}
        </Stack>
        {!isDoctor && (
          <Button
            sx={{ border: 'none', background: 'radial-gradient(circle, rgba(42,182,183,1) 0%, rgba(37,154,164,1) 100%)' }}
            component={Link}
            to="/consultations/create"
            variant="contained"
            startIcon={<ConsultationIcon htmlColor="#FFF" />}
          >
            {t('page.home.consultations.createConsultationButton.label')}
          </Button>
        )}
      </Stack>
      <Box sx={{ padding: '0 24px 24px 24px' }}>
        {!consultations.length ? (
          <Stack direction="column" justifyContent="center" alignItems="center">
            <CardMedia component={NoResults} sx={{ width: 'auto', height: '250px', mt: '10px', mr: '40px' }} />
            <Typography component="div" variant="body1">
              {emptyMessage}
            </Typography>
          </Stack>
        ) : (
          <ConsultationsTable
            data={consultations}
            totalCount={totalCount}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            onAcceptConsultation={onAcceptConsultation}
            onRefuseConsultation={onRefuseConsultation}
            isDoctor={isDoctor}
          />
        )}
      </Box>
    </Card>
  )
}
